/* ============================================================
   IDEAS CAFE — <ideas-cart-rail>

   Keeps the persistent cart column in step with the cart. Listens
   for `ideas:cart:updated` from the shared IdeasCart contract and
   re-renders itself through the Section Rendering API rather than
   rebuilding markup in JavaScript — so prices, discounts and
   translations stay server-formatted.

   Also owns quantity changes for its own line items.
   ============================================================ */
(function () {
  'use strict';

  if (customElements.get('ideas-cart-rail')) return;

  var SECTION_ID = 'ideas-cart-rail';

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  class IdeasCartRail extends HTMLElement {
    connectedCallback() {
      this._onCart = this._onCart.bind(this);
      this._onClick = this._onClick.bind(this);

      document.addEventListener('ideas:cart:updated', this._onCart);
      this.addEventListener('click', this._onClick);
    }

    disconnectedCallback() {
      document.removeEventListener('ideas:cart:updated', this._onCart);
      if (this._pending) this._pending.abort();
    }

    /* ── Re-render ───────────────────────────────────────── */

    _onCart() {
      this.refresh();
    }

    refresh() {
      // A newer cart event supersedes an in-flight render.
      if (this._pending) this._pending.abort();
      var controller = new AbortController();
      this._pending = controller;

      var self = this;
      this.setAttribute('aria-busy', 'true');

      fetch(routeRoot() + '?section_id=' + SECTION_ID, { signal: controller.signal })
        .then(function (res) {
          if (!res.ok) throw new Error('Section render failed: ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-cart-rail]');
          var current = self.querySelector('[data-cart-rail]');
          if (fresh && current) current.replaceWith(fresh);
        })
        .catch(function (err) {
          if (err.name === 'AbortError') return;
          console.warn('[ideas] cart rail refresh failed', err);
        })
        .then(function () {
          self._pending = null;
          self.removeAttribute('aria-busy');
        });
    }

    /* ── Quantity ────────────────────────────────────────── */

    _onClick(event) {
      var btn = event.target.closest('[data-qty-change]');
      if (!btn) return;
      event.preventDefault();

      var line = parseInt(btn.dataset.line, 10);
      var quantity = parseInt(btn.dataset.quantity, 10);
      if (!line || isNaN(quantity) || quantity < 0) return;

      this._changeLine(line, quantity);
    }

    _changeLine(line, quantity) {
      var self = this;
      this.setAttribute('aria-busy', 'true');

      fetch(routeRoot() + 'cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ line: line, quantity: quantity })
      })
        .then(function (res) {
          return res.json().then(function (body) {
            if (!res.ok) throw new Error(body.description || body.message || 'Cart update failed');
            return body;
          });
        })
        .then(function (cart) {
          // Broadcast rather than refresh: the header count and any
          // other listener need this too, and broadcasting triggers
          // our own re-render through the same event.
          if (window.IdeasCart) window.IdeasCart.broadcast(cart);
          else self.refresh();
        })
        .catch(function (err) {
          console.warn('[ideas] line change failed', err);
          self.refresh();
        })
        .then(function () {
          self.removeAttribute('aria-busy');
        });
    }
  }

  customElements.define('ideas-cart-rail', IdeasCartRail);
})();
